import React from "react"
import PropTypes from "prop-types"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { Bounce, toast } from "react-toastify"
import { Modal, ModalBody } from "reactstrap"
import { useNavigate } from "react-router-dom"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import PasswordRequirementsHint from "../../../components/OnboardingComponents/PasswordRequirementsHint"
import { post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { reportFormApiError } from "../../../helpers/formFieldErrors"
import {
  getActiveSessionSnapshot,
  markUnloadHandled,
  pauseActiveSession,
} from "../../../helpers/activeSession"
import { performAppLogout } from "../../../helpers/sessionLogout"
import { TIMELOG_REASONS } from "../../../helpers/sessionDetail"
import {
  PASSWORD_COMPLEXITY,
  PASSWORD_REQUIREMENTS,
  getPasswordFieldError,
  getPasswordHelperTone,
} from "../../../helpers/passwordRules"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const passwordInitialValues = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
}

const passwordValidationSchema = yup.object().shape({
  currentPassword: yup.string().required("Current password is required"),
  newPassword: yup
    .string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(PASSWORD_COMPLEXITY, PASSWORD_REQUIREMENTS)
    .notOneOf(
      [yup.ref("currentPassword")],
      "New password must be different from current password",
    ),
  confirmPassword: yup
    .string()
    .required("Please confirm your new password")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
})

/**
 * Same change-password flow as member Edit Profile
 * (`POST /api/v1/user/update/password` → logout).
 */
const ChangePasswordModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate()

  const handlePasswordSubmit = async (
    values,
    { setSubmitting, setFieldError, setFieldTouched },
  ) => {
    try {
      const response = await post(
        endpoints.updatePassword,
        {
          password: values.newPassword,
          password_confirmation: values.confirmPassword,
          old_password: values.currentPassword,
          logout_other_devices: true,
        },
        { skipErrorToast: true },
      )

      if (response?.data?.success) {
        toast.success(
          response.data.message || "Password updated successfully.",
          TOAST_OPTIONS,
        )

        markUnloadHandled()
        const active = getActiveSessionSnapshot()
        await performAppLogout({
          notifyServer: true,
          pauseActive: active.sessionId
            ? () =>
                pauseActiveSession({
                  sessionId: active.sessionId,
                  shouldPause: active.shouldPause,
                  reason: TIMELOG_REASONS.logout,
                })
            : null,
        })
        navigate("/login", { replace: true })
        return
      }

      if (response) {
        reportFormApiError(
          response,
          "Failed to update password",
          { setFieldError, setFieldTouched },
          {
            old_password: "currentPassword",
            password: "newPassword",
            password_confirmation: "confirmPassword",
          },
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--password"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body edit-profile__modal-body">
        <Formik
          key={isOpen ? "password-open" : "password-closed"}
          initialValues={passwordInitialValues}
          validationSchema={passwordValidationSchema}
          validateOnChange
          validateOnBlur
          onSubmit={handlePasswordSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldTouched,
            isSubmitting,
          }) => (
              <Form className="edit-profile__modal-form">
                <div className="edit-profile__modal-main">
                  <h3 className="edit-profile__modal-title">Change Password</h3>
                  <div className="edit-profile__modal-fields">
                    <CustomInput
                      label="Current Password"
                      type="password"
                      name="currentPassword"
                      placeholder="Enter current password"
                      value={values.currentPassword}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched("currentPassword", true)}
                      error={touched.currentPassword && errors.currentPassword}
                      autoComplete="current-password"
                    />
                    <div className="edit-profile__password-field">
                      <CustomInput
                        label="Create New Password"
                        type="password"
                        name="newPassword"
                        placeholder="Enter new password"
                        value={values.newPassword}
                        onChange={event => {
                          handleChange(event)
                          setFieldTouched("newPassword", true, false)
                        }}
                        onBlur={() => setFieldTouched("newPassword", true)}
                        error={getPasswordFieldError(
                          touched.newPassword,
                          errors.newPassword,
                        )}
                        helperTone={getPasswordHelperTone(values.newPassword)}
                        autoComplete="new-password"
                      />
                      <PasswordRequirementsHint value={values.newPassword} />
                    </div>
                    <CustomInput
                      label="Confirm New Password"
                      type="password"
                      name="confirmPassword"
                      placeholder="Confirm new password"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched("confirmPassword", true)}
                      error={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                <div className="edit-profile__modal-actions">
                  <CustomButton
                    variant="secondary"
                    size="md"
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    variant="primary"
                    size="md"
                    type="submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    Update Password
                  </CustomButton>
                </div>
              </Form>
            )
          }
        </Formik>
      </ModalBody>
    </Modal>
  )
}

ChangePasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default ChangePasswordModal
